import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useParams, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import LoadingScreen from '../../components/LoadingScreen';
import supabase from '../../utils/supabaseClient';

// Development stage display names
const stageTitles = {
  'discovery': 'Discovery',
  'preclinical': 'Preclinical',
  'phase_i': 'Phase I',
  'phase_ii': 'Phase II',
  'phase_iii': 'Phase III',
  'fda_review': 'FDA Review',
  'approved': 'Approved'
};

// Development stage descriptions
const stageDescriptions = {
  'discovery': 'The Discovery stage is the initial phase of product development where concepts are identified and validated.',
  'preclinical': 'The Preclinical stage involves testing in laboratory and animal studies to gather data on efficacy and safety.',
  'phase_i': 'Phase I trials focus on testing the safety, dosage, and side effects in a small group of human volunteers.',
  'phase_ii': 'Phase II trials expand testing to a larger group to further evaluate efficacy and monitor for side effects.',
  'phase_iii': 'Phase III trials involve large-scale testing across multiple sites to confirm effectiveness, monitor side effects, and collect data for regulatory approval.',
  'fda_review': 'During FDA Review, the regulatory agency examines all submitted data to determine if the product is safe and effective for its intended use.',
  'approved': 'The Approved stage indicates the product has received regulatory approval and is cleared for marketing.'
};

const StageInputPage = () => {
  const { stage } = useParams();
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [name, setName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  
  // Determine product category from URL path or state
  const [productCategory, setProductCategory] = useState('Therapeutics');
  
  useEffect(() => {
    // Extract product category from path if it exists
    const path = location.pathname;
    if (path.includes('vaccines')) {
      setProductCategory('Vaccines');
    }
  }, [location.pathname]);
  
  // Get properly formatted stage title
  const stageTitle = stageTitles[stage.toLowerCase()] || stage;
  const stageDescription = stageDescriptions[stage.toLowerCase()] || '';

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!name.trim()) {
      setError('Product name is required');
      return;
    }
    
    try {
      setIsSubmitting(true);
      setError(null);
      
      // Get current date in ISO format
      const currentDate = new Date().toISOString();
      
      // Format the development_stage to match the backend's expected format
      // Make sure the first letter is capitalized for the database
      let apiStage;
      if (stage.toLowerCase() === 'discovery') {
        apiStage = 'Discovery';
      } else if (stage.toLowerCase() === 'preclinical') {
        apiStage = 'Preclinical';
      } else if (stage.toLowerCase() === 'phase_i') {
        apiStage = 'Phase_I';
      } else if (stage.toLowerCase() === 'phase_ii') {
        apiStage = 'Phase_II';
      } else if (stage.toLowerCase() === 'phase_iii') {
        apiStage = 'Phase_III';
      } else if (stage.toLowerCase() === 'fda_review') {
        apiStage = 'FDA_Review';
      } else if (stage.toLowerCase() === 'approved') {
        apiStage = 'Approved';
      } else {
        // Default fallback - use the stage name with first letter capitalized
        apiStage = stage.charAt(0).toUpperCase() + stage.slice(1);
      }
      
      // Create the initial product with just the name and stage
      const { data, error: supabaseError } = await supabase
        .from('products')
        .insert([
          {
            name,
            development_stage: apiStage,
            concept_description: '',
            indications_of_use: '',
            created_at: currentDate,
            updated_at: currentDate,
            user_id: user.id,
            product_category: productCategory
          }
        ])
        .select();
      
      if (supabaseError) throw supabaseError;
      
      // Ensure correct formatting of the stage name for redirection
      let detailsStage = stage.toLowerCase();
      
      // Navigate to the stage-specific details input page
      navigate(`/products/${data[0].id}/details/${detailsStage}`);
      
    } catch (err) {
      console.error('Error creating product:', err);
      setError('Failed to create product. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return <LoadingScreen message="Loading..." />;
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-6">
        <Link to="/products/create" className="text-primary-600 hover:text-primary-700 flex items-center">
          <svg className="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18"></path>
          </svg>
          Back to Stage Selection
        </Link>
      </div>
      
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Create {stageTitle} Stage {productCategory} Product</h1>
        <p className="text-gray-600">{stageDescription}</p>
      </div>
      
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6" role="alert">
          <p>{error}</p>
        </div>
      )}
      
      <div className="bg-white shadow-md rounded-lg overflow-hidden">
        <form onSubmit={handleSubmit}>
          <div className="p-6 border-b">
            <div className="mb-6">
              <label htmlFor="name" className="block text-gray-700 text-sm font-bold mb-2">
                Product Name<span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                placeholder="Enter the product name"
                required
              />
            </div>
            
            <div className="bg-blue-50 p-4 rounded-md">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-blue-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-blue-700">
                    Enter the product name to create your {productCategory.toLowerCase()} product. You'll be able to add stage-specific details in the next step.
                  </p>
                </div>
              </div>
            </div>
          </div>
          
          <div className="bg-gray-50 px-6 py-4 flex justify-end">
            <button
              type="button"
              onClick={() => navigate('/products/create')}
              className="btn-secondary mr-4"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Creating...' : 'Create Product & Continue'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default StageInputPage; 